import React, { useState, useMemo } from "react";
import { TrendingUpIcon, ShoppingBagIcon, DollarSignIcon, PackageIcon } from "./Icons";
import { Link } from "react-router-dom";

function SalesPerformanceChart({
  chartData = [],
  kpis = {},
  inrFormatter,
  onAddProduct
}) {
  const [activeMetric, setActiveMetric] = useState("sales"); // "sales" | "orders" | "earnings"
  const [activeFilter, setActiveFilter] = useState("30d"); // "7d" | "30d" | "3m" | "12m"
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Filter data according to active timeframe
  const filteredData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    if (activeFilter === "7d") return chartData.slice(-7);
    if (activeFilter === "30d") return chartData.slice(-30);
    if (activeFilter === "3m") return chartData.slice(-90);
    return chartData;
  }, [chartData, activeFilter]);

  const totalPeriodSales = useMemo(() => {
    return filteredData.reduce((acc, d) => acc + (d.sales || 0), 0);
  }, [filteredData]);

  const totalPeriodOrders = useMemo(() => {
    return filteredData.reduce((acc, d) => acc + (d.orders || 0), 0);
  }, [filteredData]);

  const totalPeriodEarnings = useMemo(() => {
    return filteredData.reduce((acc, d) => acc + (d.sellerEarnings || 0), 0);
  }, [filteredData]);

  const hasSalesActivity = totalPeriodSales > 0 || totalPeriodOrders > 0;

  // Chart dimensions & SVG path calculation
  const width = 640;
  const height = 200;
  const paddingX = 24;
  const paddingY = 20;

  const points = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    const values = filteredData.map((d) => {
      if (activeMetric === "orders") return d.orders || 0;
      if (activeMetric === "earnings") return d.sellerEarnings || 0;
      return d.sales || 0;
    });

    const maxValue = Math.max(...values, 10);
    const stepX = (width - paddingX * 2) / Math.max(1, filteredData.length - 1);

    return filteredData.map((d, i) => {
      const val = activeMetric === "orders" ? d.orders || 0 : activeMetric === "earnings" ? d.sellerEarnings || 0 : d.sales || 0;
      const x = paddingX + i * stepX;
      const y = height - paddingY - (val / maxValue) * (height - paddingY * 2);
      return { x, y, val, label: d.label, date: d.date, raw: d };
    });
  }, [filteredData, activeMetric, width, height, paddingX, paddingY]);

  // Construct SVG smooth area and line path
  const { linePath, areaPath } = useMemo(() => {
    if (points.length === 0) return { linePath: "", areaPath: "" };
    if (points.length === 1) {
      const p = points[0];
      return {
        linePath: `M ${p.x} ${p.y} L ${width - paddingX} ${p.y}`,
        areaPath: `M ${p.x} ${p.y} L ${width - paddingX} ${p.y} L ${width - paddingX} ${height - paddingY} L ${p.x} ${height - paddingY} Z`
      };
    }

    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const cx = (p0.x + p1.x) / 2;
      d += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`;
    }

    const a = `${d} L ${points[points.length - 1].x} ${height - paddingY} L ${points[0].x} ${height - paddingY} Z`;
    return { linePath: d, areaPath: a };
  }, [points, width, height, paddingX, paddingY]);

  const currentDisplayValue =
    activeMetric === "orders"
      ? `${totalPeriodOrders} Orders`
      : activeMetric === "earnings"
      ? inrFormatter.format(totalPeriodEarnings)
      : inrFormatter.format(totalPeriodSales);

  const growthPct = kpis.salesGrowthPct;

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e0e4ea",
        borderRadius: "14px",
        padding: "22px 24px",
        boxShadow: "0 4px 14px rgba(16,20,28,0.03)",
        display: "flex",
        flexDirection: "column",
        height: "100%"
      }}
    >
      {/* Top Header: Title, Metric Selector, Timeframe Pills */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "14px",
          marginBottom: "16px"
        }}
      >
        <div>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "800",
              color: "var(--primary)",
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              display: "block",
              marginBottom: "2px"
            }}
          >
            BUSINESS ANALYTICS
          </span>
          <h3 style={{ fontSize: "18px", fontWeight: "900", color: "var(--dark)", margin: 0 }}>
            Sales Performance
          </h3>
        </div>

        {/* Time Filters: 7D, 30D, 3M, 12M */}
        <div
          style={{
            display: "inline-flex",
            background: "#f0f2f5",
            borderRadius: "8px",
            padding: "3px",
            gap: "2px"
          }}
        >
          {["7d", "30d", "3m", "12m"].map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setActiveFilter(filter)}
              style={{
                background: activeFilter === filter ? "#ffffff" : "transparent",
                color: activeFilter === filter ? "var(--dark)" : "var(--text-muted)",
                border: "none",
                borderRadius: "6px",
                padding: "4px 10px",
                fontSize: "11.5px",
                fontWeight: activeFilter === filter ? "800" : "600",
                cursor: "pointer",
                boxShadow: activeFilter === filter ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                transition: "all 0.15s ease"
              }}
            >
              {filter.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Metric Selector Tabs & Highlight Value */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: "12px",
          paddingBottom: "14px",
          borderBottom: "1px solid #edf0f4"
        }}
      >
        {/* Metric Pills */}
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            onClick={() => setActiveMetric("sales")}
            style={{
              background: activeMetric === "sales" ? "#fff5f4" : "#fbfaf8",
              border: `1.5px solid ${activeMetric === "sales" ? "var(--primary)" : "#edf0f4"}`,
              color: activeMetric === "sales" ? "var(--primary)" : "var(--text-mid)",
              borderRadius: "8px",
              padding: "6px 14px",
              fontSize: "12.5px",
              fontWeight: "800",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <DollarSignIcon size={14} />
            <span>Gross Sales</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveMetric("orders")}
            style={{
              background: activeMetric === "orders" ? "#fff5f4" : "#fbfaf8",
              border: `1.5px solid ${activeMetric === "orders" ? "var(--primary)" : "#edf0f4"}`,
              color: activeMetric === "orders" ? "var(--primary)" : "var(--text-mid)",
              borderRadius: "8px",
              padding: "6px 14px",
              fontSize: "12.5px",
              fontWeight: "800",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <ShoppingBagIcon size={14} />
            <span>Orders</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveMetric("earnings")}
            style={{
              background: activeMetric === "earnings" ? "#ecfdf5" : "#fbfaf8",
              border: `1.5px solid ${activeMetric === "earnings" ? "#16a34a" : "#edf0f4"}`,
              color: activeMetric === "earnings" ? "#16a34a" : "var(--text-mid)",
              borderRadius: "8px",
              padding: "6px 14px",
              fontSize: "12.5px",
              fontWeight: "800",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <TrendingUpIcon size={14} />
            <span>Net Earnings</span>
          </button>
        </div>

        {/* Current Total & Period Comparison */}
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "900",
              color: activeMetric === "earnings" ? "#16a34a" : activeMetric === "sales" ? "var(--primary)" : "var(--dark)",
              lineHeight: "1.1"
            }}
          >
            {currentDisplayValue}
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text-muted)", marginTop: "2px" }}>
            {growthPct !== null ? (
              <span style={{ color: growthPct >= 0 ? "#16a34a" : "#dc2626", fontWeight: "700" }}>
                {growthPct >= 0 ? `+${growthPct}%` : `${growthPct}%`} vs previous period
              </span>
            ) : (
              <span>Last {activeFilter.toUpperCase()} store performance</span>
            )}
          </div>
        </div>
      </div>

      {/* SVG Interactive Chart / Empty State */}
      <div style={{ flex: 1, minHeight: "200px", position: "relative", marginTop: "14px" }}>
        {hasSalesActivity ? (
          <div style={{ position: "relative", width: "100%", height: "200px" }}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              style={{ width: "100%", height: "100%", overflow: "visible" }}
            >
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={activeMetric === "earnings" ? "#10b981" : "#a85050"} stopOpacity="0.28" />
                  <stop offset="100%" stopColor={activeMetric === "earnings" ? "#10b981" : "#a85050"} stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Horizontal Grid lines */}
              <line x1={paddingX} y1={paddingY} x2={width - paddingX} y2={paddingY} stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />
              <line x1={paddingX} y1={height / 2} x2={width - paddingX} y2={height / 2} stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />
              <line x1={paddingX} y1={height - paddingY} x2={width - paddingX} y2={height - paddingY} stroke="#e2e8f0" strokeWidth="1" />

              {/* Area & Line */}
              {areaPath && <path d={areaPath} fill="url(#salesGrad)" />}
              {linePath && (
                <path
                  d={linePath}
                  fill="none"
                  stroke={activeMetric === "earnings" ? "#10b981" : "#a85050"}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              )}

              {/* Hoverable Interactive Points */}
              {points.map((p, idx) => (
                <circle
                  key={idx}
                  cx={p.x}
                  cy={p.y}
                  r={hoveredPoint === idx ? 5.5 : 3}
                  fill={hoveredPoint === idx ? (activeMetric === "earnings" ? "#10b981" : "#a85050") : "#ffffff"}
                  stroke={activeMetric === "earnings" ? "#10b981" : "#a85050"}
                  strokeWidth="2"
                  style={{ cursor: "pointer", transition: "r 0.15s ease" }}
                  onMouseEnter={() => setHoveredPoint(idx)}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              ))}
            </svg>

            {/* Hover Tooltip */}
            {hoveredPoint !== null && points[hoveredPoint] && (
              <div
                style={{
                  position: "absolute",
                  left: `${(points[hoveredPoint].x / width) * 100}%`,
                  top: `${(points[hoveredPoint].y / height) * 100}%`,
                  transform: "translate(-50%, -120%)",
                  background: "#111315",
                  color: "#ffffff",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "11.5px",
                  fontWeight: "700",
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                  pointerEvents: "none",
                  zIndex: 10
                }}
              >
                <div style={{ color: "#94a3b8", fontSize: "10.5px" }}>{points[hoveredPoint].label}</div>
                <div>
                  {activeMetric === "orders"
                    ? `${points[hoveredPoint].val} Orders`
                    : inrFormatter.format(points[hoveredPoint].val)}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Guided Empty State for New Sellers */
          <div
            style={{
              height: "200px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "#fbfaf8",
              borderRadius: "10px",
              border: "1px dashed #e2e8f0",
              textAlign: "center",
              padding: "20px"
            }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                background: "#fff5f4",
                color: "var(--primary)",
                display: "grid",
                placeItems: "center",
                marginBottom: "10px"
              }}
            >
              <PackageIcon size={22} />
            </div>
            <strong style={{ fontSize: "14px", color: "var(--dark)", marginBottom: "4px" }}>
              No Sales Activity Yet
            </strong>
            <p style={{ fontSize: "12.5px", color: "var(--text-muted)", maxWidth: "340px", margin: "0 0 14px" }}>
              Your daily sales and revenue graph will populate automatically as local customers place orders from your store.
            </p>
            <Link
              to="/vendor/products/new"
              className="btn btn-primary"
              style={{ padding: "6px 16px", fontSize: "12px", fontWeight: "800" }}
            >
              + List Your Next Product
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesPerformanceChart;
